import { useState, useCallback } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  ChevronLeft,
  CheckCircle2,
  Clock,
  Gauge,
  Loader2,
  Mail,
  ReceiptEuro,
  Scan,
  Snowflake,
  Sparkles,
  Sun,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { FileDropzone, type DropzoneUploadedFile } from './FileDropzone';
import { useComparateurStore } from './useComparateurStore';
import { StepEyebrow } from './StepEyebrow';
import { extractBillInsights } from '../../../lib/bill-extract';
import { resolveCspeEligibility } from '@shared/constants/cspe-eligibility';
import {
  compareBills,
  compareToMarket,
  computeSectorBenchmark,
  type BillInsights,
} from '@shared/analysis/bill-insights';
import { cn } from '../../../lib/cn';

type InsightsState =
  | { status: 'idle' }
  | { status: 'analyzing'; season: 'summer' | 'winter' }
  | { status: 'done'; season: 'summer' | 'winter'; insights: BillInsights }
  | { status: 'error' };

export function StepBills() {
  const store = useComparateurStore();
  const summer = store.dossier?.bills.summer ?? null;
  const winter = store.dossier?.bills.winter ?? null;
  const nafCode = store.dossier?.company?.nafCode ?? null;
  const employeeCount = store.dossier?.company?.employeeCount ?? null;

  const [remindLoading, setRemindLoading] = useState(false);
  const [insightsState, setInsightsState] = useState<InsightsState>({ status: 'idle' });

  // Conserver les insights des deux saisons pour le comparatif
  const [summerInsights, setSummerInsights] = useState<BillInsights | null>(
    (store.dossier?.billInsights?.summer as BillInsights | null) ?? null,
  );
  const [winterInsights, setWinterInsights] = useState<BillInsights | null>(
    (store.dossier?.billInsights?.winter as BillInsights | null) ?? null,
  );

  const hasOne = Boolean(summer) || Boolean(winter);

  const handleUpload = useCallback(
    async (season: 'summer' | 'winter', file: File) => {
      setInsightsState({ status: 'analyzing', season });

      const [, result] = await Promise.allSettled([
        store.uploadBill(season, file),
        extractBillInsights(file),
      ]);

      const insights = result.status === 'fulfilled' ? result.value : null;

      if (insights) {
        setInsightsState({ status: 'done', season, insights });
        if (season === 'summer') setSummerInsights(insights);
        else setWinterInsights(insights);
        store.saveBillInsights({ season, insights }).catch(() => {});
      } else {
        setInsightsState({ status: 'error' });
      }
    },
    [store],
  );

  async function handleRemindLater() {
    if (!store.dossier?.contact?.email) {
      store.setError("Renseignez votre email à l'étape précédente avant de demander un rappel.");
      return;
    }
    setRemindLoading(true);
    try {
      await store.requestRemindLater();
    } catch {
      /* handled in store */
    } finally {
      setRemindLoading(false);
    }
  }

  // Comparaison saisonnière disponible si les deux factures sont analysées
  const seasonalComp =
    summerInsights && winterInsights
      ? compareBills(summerInsights, winterInsights)
      : null;

  return (
    <div>
      <button
        type="button"
        onClick={() => store.goBack()}
        className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Retour
      </button>

      <StepEyebrow step="bills" />
      <h3 className="mt-2 h-section">
        Vos factures été &amp; hiver — facultatif
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Une facture estivale et une hivernale permettent d&apos;affiner votre profil et de
        détecter automatiquement les leviers d&apos;économie. PDF, photo ou scan.
      </p>

      {store.error && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[12px]">{store.error}</AlertDescription>
        </Alert>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <BillSlot
          title="Facture d'été"
          season="summer"
          icon={<Sun className="h-4 w-4 text-accent-600" />}
          hint="Mois de consommation faible (juillet–août)."
          uploaded={summer as DropzoneUploadedFile | null}
          onUpload={(file) => handleUpload('summer', file)}
          analyzed={Boolean(summerInsights)}
        />
        <BillSlot
          title="Facture d'hiver"
          season="winter"
          icon={<Snowflake className="h-4 w-4 text-brand-600" />}
          hint="Mois de pointe (janvier–février)."
          uploaded={winter as DropzoneUploadedFile | null}
          onUpload={(file) => handleUpload('winter', file)}
          analyzed={Boolean(winterInsights)}
        />
      </div>

      {/* Carte analyse en direct de la dernière facture uploadée */}
      <BillInsightsCard
        state={insightsState}
        nafCode={nafCode}
        employeeCount={employeeCount}
      />

      {/* Comparaison saisonnière si les deux factures sont disponibles */}
      {seasonalComp && <SeasonalComparisonCard comparison={seasonalComp} />}

      <div className="mt-6 rounded-xl border border-stone-200 bg-stone-50/60 p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_1px_3px_rgba(0,0,0,.04)]">
            <CalendarClock className="h-4 w-4 text-stone-500" strokeWidth={1.7} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-ink">Pas de factures sous la main ?</p>
            <p className="mt-1 text-[12px] leading-relaxed text-stone-600">
              Recevez par e-mail un lien personnel pour reprendre votre dossier plus tard.
              Vos données saisies sont conservées 30 jours.
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleRemindLater}
              disabled={remindLoading || !store.dossier?.contact?.email}
              className="mt-3 gap-1.5"
            >
              {remindLoading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Envoi du lien…
                </>
              ) : (
                <>
                  <Mail className="h-3.5 w-3.5" />
                  M&apos;envoyer le lien de reprise
                </>
              )}
            </Button>
            {store.dossier?.bills.requested && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-700">
                <Mail className="h-3 w-3" />
                Lien envoyé — vous pouvez aussi continuer maintenant.
              </p>
            )}
          </div>
        </div>
      </div>

      {!hasOne && !store.dossier?.bills.requested && (
        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-700" />
          <p className="text-[12px] leading-relaxed text-yellow-900">
            <strong>Optionnel mais recommandé.</strong> Sans facture, nous pouvons toujours
            vous recontacter ; nos consultants vous demanderont les éléments par téléphone.
          </p>
        </div>
      )}

      <Button
        type="button"
        variant="brand"
        size="xl"
        onClick={() => store.setStep('review')}
        disabled={store.loading}
        className="mt-6 w-full gap-2"
      >
        {store.loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Patientez…
          </>
        ) : (
          <>
            {hasOne ? 'Vérifier ma demande' : 'Continuer sans facture'}
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Slot de dépôt de facture
──────────────────────────────────────────────────────────────────────────── */

function BillSlot(props: {
  title: string;
  season: 'summer' | 'winter';
  icon: React.ReactNode;
  hint: string;
  uploaded: DropzoneUploadedFile | null;
  onUpload: (file: File) => Promise<void>;
  analyzed?: boolean;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white shadow-[0_1px_3px_rgba(0,0,0,.06)]">
          {props.icon}
        </span>
        <p className="text-[13px] font-semibold text-ink">{props.title}</p>
        {props.analyzed && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-brand-700">
            <Sparkles className="h-2.5 w-2.5" />
            Analysée
          </span>
        )}
      </div>
      <FileDropzone
        season={props.season}
        label={`Importer la ${props.title.toLowerCase()}`}
        helperText={props.hint}
        uploaded={props.uploaded}
        onUpload={props.onUpload}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Carte d'analyse en direct (dernière facture uploadée)
──────────────────────────────────────────────────────────────────────────── */

function BillInsightsCard({
  state,
  nafCode,
  employeeCount,
}: {
  state: InsightsState;
  nafCode: string | null;
  employeeCount: string | null;
}) {
  if (state.status === 'idle') return null;

  if (state.status === 'analyzing') {
    return (
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-brand-100 bg-brand-50/60 px-4 py-3">
        <Scan className="h-4 w-4 animate-pulse text-brand-600" strokeWidth={1.7} />
        <p className="text-[13px] text-brand-900">
          <span className="font-semibold">Analyse en cours…</span>{' '}
          <span className="text-brand-700">Extraction des données de votre facture.</span>
        </p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50/60 px-4 py-3">
        <p className="text-[12px] text-stone-500">
          Analyse non concluante — un consultant lira votre facture lors de l&apos;échange.
        </p>
      </div>
    );
  }

  const { insights } = state;
  const cspeEligibility = resolveCspeEligibility(nafCode);
  const showCspe = insights.taxes.cspe && cspeEligibility !== null;
  const showTurpe = insights.taxes.turpe;

  const marketComp = compareToMarket(insights.unitPriceEurPerKwh, insights.energyType);
  const benchmark = computeSectorBenchmark(nafCode, insights.annualConsumptionKwh, employeeCount);

  const dataItems: Array<{ icon: LucideIcon; label: string; value: string; sub?: string }> = [];

  if (insights.supplierName) {
    dataItems.push({ icon: Zap, label: 'Fournisseur', value: insights.supplierName });
  }
  if (insights.annualConsumptionKwh) {
    dataItems.push({
      icon: Zap,
      label: 'Consommation',
      value: `${insights.annualConsumptionKwh.toLocaleString('fr-FR')} kWh`,
    });
  }
  if (insights.subscribedPowerKva) {
    dataItems.push({
      icon: Gauge,
      label: 'Puissance souscrite',
      value: `${insights.subscribedPowerKva} kVA`,
      sub: insights.overPowerSignal ? 'Potentiellement surdimensionnée' : undefined,
    });
  }
  if (insights.totalAmountEur) {
    dataItems.push({
      icon: ReceiptEuro,
      label: 'Montant TTC',
      value: `${insights.totalAmountEur.toLocaleString('fr-FR')} €`,
    });
  }
  if (insights.unitPriceEurPerKwh) {
    dataItems.push({
      icon: ReceiptEuro,
      label: 'Prix unitaire HT',
      value: `${(insights.unitPriceEurPerKwh * 100).toFixed(2)} c€/kWh`,
      sub: marketComp
        ? marketComp.level === 'cheap'
          ? 'En-dessous du marché'
          : marketComp.level === 'expensive'
            ? `+${Math.round(marketComp.overCostRatio * 100)} % vs marché`
            : marketComp.level === 'very_expensive'
              ? `+${Math.round(marketComp.overCostRatio * 100)} % vs marché ⚠`
              : 'Dans la moyenne marché'
        : undefined,
    });
  }
  if (insights.contractExpiryDate) {
    dataItems.push({
      icon: Clock,
      label: 'Fin de contrat',
      value: new Date(insights.contractExpiryDate).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    });
  }

  const contractLabels: Record<string, string> = {
    fixed: 'Prix fixé',
    indexed: 'Indexé marché',
    regulated: 'Tarif réglementé',
  };
  const contractLabel =
    insights.contractType !== 'unknown' ? contractLabels[insights.contractType] : null;

  return (
    <div className="mt-4 rounded-xl border border-brand-200 bg-brand-50/50 p-4">
      {/* En-tête */}
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-brand-600" strokeWidth={1.7} />
        <p className="text-[13px] font-semibold text-brand-900">Analyse de votre facture</p>
        <div className="ml-auto flex items-center gap-2">
          {contractLabel && (
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
              {contractLabel}
            </span>
          )}
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]',
              insights.confidence === 'high'
                ? 'bg-brand-100 text-brand-700'
                : insights.confidence === 'medium'
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-stone-100 text-stone-600',
            )}
          >
            {insights.confidence === 'high' ? 'Fiable' : insights.confidence === 'medium' ? 'Partiel' : 'Indicatif'}
          </span>
        </div>
      </div>

      {/* Score potentiel d'économie */}
      {insights.savingsScore > 0 && (
        <SavingsScoreBar score={insights.savingsScore} />
      )}

      {/* Données extraites */}
      {dataItems.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {dataItems.map((item) => {
            const Icon = item.icon;
            const isWarning = item.sub?.includes('%');
            return (
              <div
                key={item.label}
                className={cn(
                  'flex items-start gap-2 rounded-lg border px-3 py-2',
                  isWarning
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-brand-100 bg-white',
                )}
              >
                <Icon
                  className={cn(
                    'mt-0.5 h-3.5 w-3.5 shrink-0',
                    isWarning ? 'text-yellow-600' : 'text-brand-500',
                  )}
                  strokeWidth={1.7}
                />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-stone-400">
                    {item.label}
                  </p>
                  <p className="truncate text-[12.5px] font-semibold text-ink">{item.value}</p>
                  {item.sub && (
                    <p
                      className={cn(
                        'text-[10.5px]',
                        isWarning ? 'text-yellow-700' : 'text-brand-600',
                      )}
                    >
                      {item.sub}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Benchmark sectoriel */}
      {benchmark && (
        <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-stone-200 bg-white px-3 py-2">
          <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" strokeWidth={1.7} />
          <div>
            <p className="text-[11px] font-semibold text-stone-700">
              Benchmark sectoriel — consommation {benchmark.interpretation === 'above_median' ? 'supérieure' : benchmark.interpretation === 'below_median' ? 'inférieure' : 'conforme'} à la médiane
            </p>
            {benchmark.ratio !== null && (
              <p className="text-[11px] text-stone-500">
                Médiane secteur estimée : {benchmark.medianKwh.toLocaleString('fr-FR')} kWh/an
                {benchmark.ratio > 1.2 && (
                  <span className="ml-1 font-semibold text-yellow-700">
                    ({Math.round((benchmark.ratio - 1) * 100)} % au-dessus)
                  </span>
                )}
                {benchmark.ratio < 0.8 && (
                  <span className="ml-1 font-semibold text-brand-700">
                    ({Math.round((1 - benchmark.ratio) * 100)} % en-dessous)
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Leviers détectés */}
      {(showTurpe || showCspe || insights.overPowerSignal) && (
        <div className="mt-3 space-y-2">
          {insights.overPowerSignal && (
            <InsightLeverBadge
              icon={TriangleAlert}
              label="Sur-puissance souscrite détectée"
              hint="Votre puissance souscrite semble élevée par rapport à votre consommation — un re-dimensionnement TURPE peut générer des économies immédiates."
              variant="warning"
            />
          )}
          {showTurpe && !insights.overPowerSignal && (
            <InsightLeverBadge
              icon={Gauge}
              label="Optimisation TURPE détectée"
              hint="Votre facture inclut une ligne d'acheminement — notre courtier peut analyser si votre puissance souscrite est optimale."
            />
          )}
          {showCspe && cspeEligibility && (
            <InsightLeverBadge
              icon={ReceiptEuro}
              label={`Remboursement CSPE — ${cspeEligibility.label}`}
              hint={cspeEligibility.hint}
              level={cspeEligibility.level}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Jauge score d'économie
──────────────────────────────────────────────────────────────────────────── */

function SavingsScoreBar({ score }: { score: number }) {
  const label =
    score >= 70
      ? 'Fort potentiel'
      : score >= 40
        ? 'Potentiel moyen'
        : 'Potentiel limité';
  const colorBar =
    score >= 70
      ? 'bg-accent-500'
      : score >= 40
        ? 'bg-brand-500'
        : 'bg-stone-400';
  const colorText =
    score >= 70 ? 'text-accent-800' : score >= 40 ? 'text-brand-800' : 'text-stone-600';

  return (
    <div className="rounded-lg border border-brand-100 bg-white px-3 py-2.5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-400">
          Potentiel d&apos;économie estimé
        </p>
        <p className={cn('text-[12px] font-bold', colorText)}>
          {label} — {score}/100
        </p>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-stone-100">
        <div
          className={cn('h-full rounded-full transition-all duration-700', colorBar)}
          style={{ width: `${score}%` }}
        />
      </div>
      <p className="mt-1 text-[10.5px] text-stone-500">
        Basé sur les leviers détectés (TURPE, CSPE, type contrat, prix marché, puissance)
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Carte comparaison saisonnière été / hiver
──────────────────────────────────────────────────────────────────────────── */

function SeasonalComparisonCard({
  comparison,
}: {
  comparison: ReturnType<typeof compareBills>;
}) {
  if (!comparison) return null;

  const { summerKwh, winterKwh, variationPct, profile, consultantNote } = comparison;
  const winterHigher = winterKwh > summerKwh;
  const Icon = winterHigher ? TrendingUp : TrendingDown;

  const profileLabels: Record<string, string> = {
    heating_dominated: 'Profil chauffage',
    cooling_dominated: 'Profil climatisation / froid',
    stable: 'Profil stable',
    unknown: 'Profil à analyser',
  };

  return (
    <div className="mt-3 rounded-xl border border-lavender-200 bg-lavender-50/50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-lavender-600" strokeWidth={1.7} />
        <p className="text-[13px] font-semibold text-lavender-900">Comparaison été / hiver</p>
        <span className="ml-auto rounded-full bg-lavender-100 px-2 py-0.5 text-[10px] font-semibold text-lavender-700">
          {profileLabels[profile]}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-lavender-100 bg-white px-3 py-2">
          <div className="flex items-center gap-1.5">
            <Sun className="h-3.5 w-3.5 text-accent-500" strokeWidth={1.7} />
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-stone-400">Été</p>
          </div>
          <p className="mt-0.5 text-[13px] font-bold text-ink">
            {summerKwh.toLocaleString('fr-FR')} kWh
          </p>
        </div>
        <div className="rounded-lg border border-lavender-100 bg-white px-3 py-2">
          <div className="flex items-center gap-1.5">
            <Snowflake className="h-3.5 w-3.5 text-brand-500" strokeWidth={1.7} />
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-stone-400">Hiver</p>
          </div>
          <p className="mt-0.5 text-[13px] font-bold text-ink">
            {winterKwh.toLocaleString('fr-FR')} kWh
          </p>
        </div>
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
            winterHigher ? 'bg-lavender-100 text-lavender-800' : 'bg-accent-100 text-accent-800',
          )}
        >
          <TrendingDown
            className={cn('h-3 w-3', winterHigher && 'rotate-180')}
            strokeWidth={2.5}
          />
          {winterHigher ? '+' : ''}{Math.abs(Math.round(variationPct))} % {winterHigher ? 'en hiver' : 'en été'}
        </span>
      </div>

      <p className="mt-2.5 text-[11.5px] leading-relaxed text-lavender-800">{consultantNote}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Badge de levier détecté
──────────────────────────────────────────────────────────────────────────── */

function InsightLeverBadge({
  icon: Icon,
  label,
  hint,
  level,
  variant,
}: {
  icon: LucideIcon;
  label: string;
  hint: string;
  level?: 'eligible' | 'likely' | 'none';
  variant?: 'warning';
}) {
  const isWarning = variant === 'warning';
  const isEligible = level === 'eligible';
  const bgClass = isWarning
    ? 'border-yellow-200 bg-yellow-50/70'
    : isEligible
      ? 'border-accent-200 bg-accent-50/70'
      : 'border-brand-200 bg-brand-50/70';
  const iconClass = isWarning
    ? 'bg-yellow-100 text-yellow-700'
    : isEligible
      ? 'bg-accent-100 text-accent-700'
      : 'bg-brand-100 text-brand-700';
  const checkClass = isWarning ? 'text-yellow-600' : isEligible ? 'text-accent-600' : 'text-brand-600';

  return (
    <div className={cn('flex items-start gap-3 rounded-xl border p-3', bgClass)}>
      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', iconClass)}>
        <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
      </span>
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink">
          <CheckCircle2 className={cn('h-3.5 w-3.5 shrink-0', checkClass)} strokeWidth={2.5} />
          {label}
        </p>
        <p className="mt-0.5 text-[11.5px] leading-snug text-stone-600">{hint}</p>
      </div>
    </div>
  );
}
